import { useGetEstimateInfo, useUpdateEstimateInfo } from '@/hooks/useEstimate';
import Modal from '../ui/Modal';
import Loader from '../ui/Loader';
import { EstimateInfoResponse } from '@/types/estimates/estimateInfoResponse';
import {
  CreateEstimateFormData,
  CreateEstimateSchema,
} from '@/types/estimates/CreateEstimateFormData';
import { SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useToast } from '@/hooks/useToast';
import EstimateCreationInformationStep from './EstimateCreationInformationStep';
import { useTranslation } from '@/i18n';
import { resolveMarginRateForApiUpdate } from '@/types/common/margin-rate.schema';
import {
  estimateFlexibilityToFormValues,
  flexibilityFormValuesToApi,
} from '@/utils/estimates/estimateFlexibilityFormValues';

interface UpdateEstimateInfoModalProps {
  estimateId: string;
  projectId: string;
  closeModal: () => void;
}

const UpdateEstimateInfoModal = ({
  estimateId,
  projectId,
  closeModal,
}: UpdateEstimateInfoModalProps) => {
  const { t } = useTranslation();
  const {
    data: estimateInfo,
    isLoading,
    isRefetching,
    isSuccess,
  } = useGetEstimateInfo(estimateId, projectId);

  return isLoading || isRefetching ? (
    <Modal
      title={t('estimates.modals.updateEstimateInfo.title')}
      closeModal={closeModal}
      actionTitle={t('estimates.modals.updateEstimateInfo.actionTitle')}
      isActionDisabled
      actionOnClick={closeModal}
    >
      <Loader />
    </Modal>
  ) : estimateInfo ? (
    isSuccess && (
      <EstimateInfoFormModal
        estimateId={estimateId}
        projectId={projectId}
        closeModal={closeModal}
        estimateInfo={estimateInfo}
      />
    )
  ) : (
    <Modal
      title={t('estimates.modals.updateEstimateInfo.title')}
      closeModal={closeModal}
      actionTitle={t('common.cancel')}
      actionOnClick={closeModal}
      isActionDisabled
      hasAction={false}
      message={t('common.errorOccurred')}
    />
  );
};

export default UpdateEstimateInfoModal;

interface EstimateInfoFormProps {
  estimateId: string;
  projectId: string;
  closeModal: () => void;
  estimateInfo: EstimateInfoResponse;
}

const EstimateInfoFormModal = ({
  closeModal,
  estimateId,
  estimateInfo,
  projectId,
}: EstimateInfoFormProps) => {
  const { t } = useTranslation();
  const methods = useForm<CreateEstimateFormData>({
    mode: 'onChange',
    resolver: zodResolver(CreateEstimateSchema),
    defaultValues: {
      marginRate: estimateInfo.marginRate ?? undefined,
      name: estimateInfo.name ?? undefined,
      ...estimateFlexibilityToFormValues(estimateInfo),
      adults: estimateInfo.adults.map((adult) => ({
        firstName: adult.firstName ?? undefined,
        lastName: adult.lastName ?? undefined,
        isSelected: true,
      })),
      children: estimateInfo.children.map((child) => ({
        firstName: child.firstName ?? undefined,
        lastName: child.lastName ?? undefined,
        birthDate: child.birthDate ? new Date(child.birthDate) : undefined,
        isSelected: true,
      })),
    },
  });

  const {
    control,
    handleSubmit,
    formState: { isValid, isDirty, dirtyFields },
  } = methods;

  const { mutateAsync: updateEstimateInfo } = useUpdateEstimateInfo(
    estimateId,
    projectId,
  );
  const { toastAsync } = useToast();

  const submitForm: SubmitHandler<CreateEstimateFormData> = async (data) => {
    const { status } = await toastAsync(
      updateEstimateInfo({
        name: dirtyFields.name ? data.name : undefined,
        marginRate: resolveMarginRateForApiUpdate(
          Boolean(dirtyFields.marginRate),
          data.marginRate,
        ),
        ...flexibilityFormValuesToApi(data, {
          allowAgencyModification: dirtyFields.allowAgencyModification,
          showQuoteName: dirtyFields.showQuoteName,
          showSummaryAndImportantPoints:
            dirtyFields.showSummaryAndImportantPoints,
          showPriceIncludesExcludes: dirtyFields.showPriceIncludesExcludes,
          showModificationCancellationConditions:
            dirtyFields.showModificationCancellationConditions,
          showDates: dirtyFields.showDates,
          allowAgencyInternationalFlights:
            dirtyFields.allowAgencyInternationalFlights,
          allowAgencyMarginRate: dirtyFields.allowAgencyMarginRate,
          allowAgencyAdditionalMargin: dirtyFields.allowAgencyAdditionalMargin,
        }),
        adults: data.adults
          .map((adult, index) => ({
            id:
              estimateInfo.adults.length > index
                ? estimateInfo.adults[index].id
                : undefined,
            firstName: dirtyFields.adults?.[index]?.firstName
              ? adult.firstName
              : undefined,
            lastName: dirtyFields.adults?.[index]?.lastName
              ? adult.lastName
              : undefined,
            isSelected: adult.isSelected,
          }))
          .filter(({ isSelected }) => isSelected)
          .map((adult) => ({ ...adult, isSelected: undefined })),
        children: data.children
          .map((child, index) => ({
            id:
              estimateInfo.children.length > index
                ? estimateInfo.children[index].id
                : undefined,
            firstName: dirtyFields.children?.[index]?.firstName
              ? child.firstName
              : undefined,
            lastName: dirtyFields.children?.[index]?.lastName
              ? child.lastName
              : undefined,
            birthDate: dirtyFields.children?.[index]?.birthDate
              ? child.birthDate
              : undefined,
            isSelected: child.isSelected,
          }))
          .filter(({ isSelected }) => isSelected)
          .map((child) => ({ ...child, isSelected: undefined })),
      }),
      {
        success: t('estimates.modals.updateEstimateInfo.success'),
      },
    );

    if (status < 300) closeModal();
  };

  return (
    <Modal
      title={t('estimates.modals.updateEstimateInfo.title')}
      closeModal={closeModal}
      actionTitle={t('estimates.modals.updateEstimateInfo.actionTitle')}
      isActionDisabled={!isDirty || !isValid}
      actionOnClick={handleSubmit(submitForm)}
    >
      <EstimateCreationInformationStep
        circuitId={estimateInfo.circuitId}
        projectId={projectId}
        control={control}
        clientId={estimateInfo.project.clientId}
        estimateInfo={estimateInfo}
      />
    </Modal>
  );
};
